import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { COURSES } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const badgeColors: Record<string, string> = {
  gold: "bg-[#c8a96e] text-[#2c1f0e]",
  brown: "bg-[#2c1f0e] text-[#c8a96e] border border-[#c8a96e]/40",
  green: "bg-[#2d4a2d] text-[#8bc88b]",
  rose: "bg-[#4a1a2a] text-[#e8a0b0]",
};

const certBgColors: Record<string, string> = {
  gold: "from-[#c8a96e]/10 to-[#c8a96e]/5 border-[#c8a96e]/25",
  brown: "from-[#2c1f0e]/8 to-[#2c1f0e]/4 border-[#2c1f0e]/20",
  green: "from-[#2d4a2d]/10 to-[#2d4a2d]/5 border-[#5a8a5a]/20",
  rose: "from-[#4a1a2a]/10 to-[#4a1a2a]/5 border-[#8a4a5a]/20",
};

function CourseCard({ course }: { course: typeof COURSES[number] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      variants={fadeUp}
      className={`rounded-3xl overflow-hidden border bg-white transition-shadow duration-300 hover:shadow-xl ${
        course.highlight ? "col-span-1 md:col-span-2" : ""
      }`}
      style={{
        boxShadow: "0 4px 24px -4px rgba(44,31,14,0.10)",
        borderColor: course.highlight ? "rgba(200,169,110,0.35)" : "rgba(44,31,14,0.08)",
      }}
    >
      {course.highlight ? (
        /* ── Featured layout (wide) ── */
        <div className="grid md:grid-cols-5">
          <div className="md:col-span-2 relative min-h-64">
            <img src={course.image} alt={course.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#2c1f0e]/20" />
            <span className={`absolute top-4 left-4 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full ${badgeColors[course.badgeColor]}`}>
              {course.badge}
            </span>
          </div>
          <div className="md:col-span-3 p-8 md:p-12 flex flex-col justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-[#c8a96e] text-xs tracking-widest uppercase font-semibold">{course.cert}</span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-[#2c1f0e] mb-3">{course.title}</h2>
              <p className="text-[#5c4033]/70 leading-relaxed mb-6">{course.description}</p>
              <ul className="grid grid-cols-2 gap-2 mb-6">
                {course.details.map((d) => (
                  <li key={d} className="flex items-center gap-2 text-[#5c4033]/80 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#c8a96e] flex-shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#c8a96e]/10 rounded-full border border-[#c8a96e]/25">
                  <div className="w-2 h-2 rounded-full bg-[#4ade80] animate-pulse" />
                  <span className="text-[#c8a96e] text-sm font-semibold">2026/27學年 · 已於5月23日正式開課（進行中）</span>
                </div>
                <a
                  href={course.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#2c1f0e] text-[#c8a96e] px-7 py-3 rounded-full font-bold hover:bg-[#3d2b1a] transition-all duration-200"
                >
                  進入學員專區 <ArrowRight size={16} />
                </a>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#f5f0e8] rounded-full w-fit">
                <span className="text-[#8a7060] text-xs">主辦機構：HKU SPACE 香港大學持續進修學院</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ── Standard card ── */
        <div>
          <div className="relative h-52 overflow-hidden">
            <img src={course.image} alt={course.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2c1f0e]/50 to-transparent" />
            <span className={`absolute top-3 left-3 text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full ${badgeColors[course.badgeColor]}`}>
              {course.badge}
            </span>
          </div>
          <div className="p-6">
            <div className={`inline-block text-[10px] tracking-widest uppercase px-3 py-1 rounded-full mb-3 bg-gradient-to-r border ${certBgColors[course.badgeColor]}`}>
              <span className="text-[#8a7060]">{course.cert}</span>
            </div>
            <h3 className="font-serif text-xl text-[#2c1f0e] mb-2 leading-snug">{course.title}</h3>
            <p className="text-[#5c4033]/70 text-sm leading-relaxed mb-4">{course.description}</p>

            {/* Expandable details */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-[#8a7060] text-xs font-semibold uppercase tracking-wide mb-3 hover:text-[#c8a96e] transition-colors"
            >
              {expanded ? "收起" : "課程內容"} {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <AnimatePresence>
              {expanded && (
                <motion.ul
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-1.5 mb-4 overflow-hidden"
                >
                  {course.details.map((d) => (
                    <li key={d} className="flex items-center gap-2 text-[#5c4033]/80 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#c8a96e] flex-shrink-0" />
                      {d}
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>

            <div className="border-t border-[#c8a96e]/15 pt-4 flex items-center justify-between">
              <span className="text-[#8a7060] text-xs">{course.nextDate}</span>
              <a
                href={course.link}
                className="inline-flex items-center gap-1.5 border border-[#2c1f0e] text-[#2c1f0e] px-5 py-2 rounded-full text-sm font-semibold hover:bg-[#2c1f0e] hover:text-[#c8a96e] transition-all duration-200"
              >
                查詢 <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Courses() {
  const [filter, setFilter] = useState<string>("全部");
  const filters = ["全部", "RYT200", "RYT300", "YACEP", "RPYT"];

  const filtered =
    filter === "全部" ? COURSES : COURSES.filter((c) => c.badge === filter || c.code === filter);

  return (
    <div className="bg-[#fdfbf7] overflow-x-hidden">
      {/* ── HERO ─────────────────────────────────────── */}
      <section className="relative py-36 bg-gradient-to-br from-[#2c1f0e] to-[#1a1208] overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[20vw] font-serif text-[#c8a96e]/5 select-none pointer-events-none leading-none">
          OM
        </div>
        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl mx-auto">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.4em] uppercase mb-4">
              師資培訓課程
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-5xl md:text-6xl text-[#fdfbf7] mb-6">
              七大認證<br />培訓課程
            </motion.h1>
            <motion.p variants={fadeUp} className="text-[#f0e6d3]/70 text-lg leading-relaxed">
              Yoga Alliance 國際認可 · E-RYT 500 導師主理 · 逾37屆師資培訓經驗
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── FILTER TABS ──────────────────────────────── */}
      <section className="bg-white border-b border-[#c8a96e]/15 sticky top-16 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-none">
            <span className="text-[#8a7060] text-xs tracking-wide flex-shrink-0">篩選：</span>
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  filter === f
                    ? "bg-[#2c1f0e] text-[#c8a96e]"
                    : "bg-[#f5f0e8] text-[#5c4033] hover:bg-[#eae4d8]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── COURSES GRID ─────────────────────────────── */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {filtered.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────── */}
      <section className="py-20 bg-[#f5f0e8]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-3xl mx-auto"
          >
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4 text-center">
              常見問題
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl text-[#2c1f0e] text-center mb-12">
              你想知道的，都在這裡
            </motion.h2>
            {[
              {
                q: "完成課程後可以獲得什麼認證？",
                a: "視乎課程而定。RYT200/RYT300 可申請 Yoga Alliance 登記；YACEP 課程可獲持續教育學分；RPYT 為認可產前瑜伽導師資格。所有認證均為 Yoga Alliance 國際認可。",
              },
              {
                q: "課程有沒有瑜伽基礎要求？",
                a: "RYT200 建議有一定瑜伽練習基礎（約1年），但更重要的是你對學習的熱誠。YACEP 各課程一般要求持有 RYT200 或同等資格。具體要求可直接向我們查詢。",
              },
              {
                q: "學員可以使用 Thinkific 平台嗎？",
                a: "可以！所有 Abhyaas 學員均可在 Thinkific 平台（abhyaas.thinkific.com）存取課程資料、進度追蹤及各種學習工具，方便課後自學及複習。",
              },
              {
                q: "課程的語言是什麼？",
                a: "所有課程均以廣東話主講，輔以繁體中文教材。部分理論資料提供英文補充，讓學員可深入研究國際文獻。",
              },
              {
                q: "如何報名或查詢課程？",
                a: "歡迎透過電郵（abhyaas.hk@gmail.com）、WhatsApp（9293-5939）或 Instagram/Facebook 私訊聯絡。我們會盡快回覆。",
              },
            ].map((faq, i) => {
              return <FaqItem key={i} q={faq.q} a={faq.a} />;
            })}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-[#2c1f0e] to-[#1a1208]">
        <div className="container mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto">
            <motion.h2 variants={fadeUp} className="font-serif text-3xl text-[#fdfbf7] mb-6">
              有任何疑問？歡迎直接聯絡
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#f0e6d3]/70 mb-8">
              每一個問題都值得被認真回答。我們在。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:abhyaas.hk@gmail.com"
                className="inline-flex items-center gap-2 bg-[#c8a96e] text-[#2c1f0e] px-8 py-4 rounded-full font-bold hover:bg-[#d4b87c] transition-colors"
              >
                傳送電郵 <ArrowRight size={18} />
              </a>
              <a
                href="https://wa.me/85292935939"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#c8a96e]/60 text-[#c8a96e] px-8 py-4 rounded-full font-semibold hover:bg-[#c8a96e]/10 transition-colors"
              >
                WhatsApp 查詢
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      variants={fadeUp}
      className="border-b border-[#c8a96e]/20 py-5"
    >
      <button
        className="w-full flex items-start justify-between gap-4 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-serif text-[#2c1f0e] text-lg leading-snug">{q}</span>
        <span className="text-[#c8a96e] flex-shrink-0 mt-1">
          {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="text-[#5c4033]/80 text-sm leading-relaxed mt-3 overflow-hidden"
          >
            {a}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
